import React, { useState, useEffect } from 'react';
import { formatIST } from '@/lib/utils';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, X, Package } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Screen, ScreenSplit, Pane, CommandBar, FactBand, Fact, SidePane } from '@/components/data';
import { getCatalogItem, deleteCatalogItem, addCustomerPrice, removeCustomerPrice, getStockTransactions, getCustomers } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { getProductTypeName } from '@/lib/constants';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/useIsMobile';
import PhoneCatalogItem from '@/pages/Catalog/PhoneCatalogItem';

const CatalogItemDetailPage = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [priceToRemove, setPriceToRemove] = useState(null);
  const [newPrice, setNewPrice] = useState({ customer_id: '', price_per_piece: '' });
  const [jobCardConfig, setJobCardConfig] = useState(null);
  const [orderSchema, setOrderSchema] = useState(null);

  useEffect(() => {
    fetchData();
  }, [itemId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Load catalog item first (critical)
      const itemData = await getCatalogItem(itemId);
      setItem(itemData);

      // Load non-critical data separately (don't fail the whole page)
      getStockTransactions({ catalog_item_id: itemId, limit: 10 })
        .then(txData => setTransactions(txData.transactions || []))
        .catch(() => setTransactions([]));

      getCustomers({ limit: 1000 })
        .then(customersData => setCustomers(customersData.customers || []))
        .catch(() => setCustomers([]));

      // Load product config and schema for proper field labels
      if (itemData.product_type) {
        api.get(`/admin/product-configs/${itemData.product_type}`)
          .then(res => setJobCardConfig(res.data))
          .catch(() => {});
        api.get('/orders/types/schemas')
          .then(res => setOrderSchema(res.data?.[itemData.product_type]))
          .catch(() => {});
      }
    } catch (error) {
      toast.error('Failed to load catalog item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => setConfirmDelete(true);

  const confirmDeleteItem = async () => {
    setConfirmDelete(false);
    try {
      await deleteCatalogItem(itemId);
      toast.success('Catalog item deleted');
      navigate('/catalog');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete item');
    }
  };

  const handleAddCustomerPrice = async () => {
    if (!newPrice.customer_id || !newPrice.price_per_piece) {
      toast.error('Please select customer and enter price');
      return;
    }
    try {
      await addCustomerPrice(itemId, {
        customer_id: newPrice.customer_id,
        price_per_piece: parseFloat(newPrice.price_per_piece)
      });
      toast.success('Customer price added');
      setShowPricingModal(false);
      setNewPrice({ customer_id: '', price_per_piece: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add customer price');
    }
  };

  const handleRemoveCustomerPrice = (customerId) => setPriceToRemove(customerId);

  const confirmRemovePrice = async () => {
    const customerId = priceToRemove;
    setPriceToRemove(null);
    if (!customerId) return;
    try {
      await removeCustomerPrice(itemId, customerId);
      toast.success('Customer price removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove customer price');
    }
  };

  // Build field label map from config
  const getFieldLabel = (fieldKey, isJobCard = false) => {
    if (isJobCard && jobCardConfig?.job_card_fields) {
      const f = jobCardConfig.job_card_fields.find(ff => ff.name === fieldKey);
      if (f) return f.label;
    }
    if (!isJobCard && orderSchema?.sections) {
      for (const sec of orderSchema.sections) {
        const f = sec.fields.find(ff => ff.name === fieldKey);
        if (f) return f.label;
      }
    }
    return fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">Item not found</h3>
          <button onClick={() => navigate('/catalog')} className="mt-4 text-brand-blue hover:underline">
            Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  const stockStatus = item.finished_goods_stock === 0 ? 'out' : 
                      item.finished_goods_stock <= item.min_stock_threshold ? 'low' : 'ok';

  const orderFields = item.order_creation_fields || {};
  const jobCardFields = item.job_card_fields || {};
  const hasOrderFields = Object.keys(orderFields).length > 0;
  const hasJobCardFields = Object.keys(jobCardFields).length > 0;

  const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  const stock = item.finished_goods_stock || 0;
  const threshold = item.min_stock_threshold || 0;
  const lowStock = threshold > 0 && stock <= threshold;

  const specRows = (fields, isJobCard) => Object.entries(fields)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([key, value]) => (
      <Fact key={key} label={getFieldLabel(key, isJobCard)}>
        {Array.isArray(value) ? value.join(', ') : String(value)}
      </Fact>
    ));

  const dialogs = (
    <>
      <AlertDialog open={confirmDelete} onOpenChange={() => setConfirmDelete(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Delete “${item.name}”?`}</AlertDialogTitle>
            <AlertDialogDescription>The product leaves the catalog. Orders already placed against it are unaffected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteItem} className="bg-red-600 text-white hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!priceToRemove} onOpenChange={() => setPriceToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this customer price?</AlertDialogTitle>
            <AlertDialogDescription>That customer goes back to paying the default price on new orders.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemovePrice} className="bg-red-600 text-white hover:bg-red-700">
              Remove price
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  // a bar, pills and cards below lg; the same state, the same confirmations
  if (isMobile) {
    return (
      <>
        <PhoneCatalogItem
          item={item} transactions={transactions} customers={customers} getFieldLabel={getFieldLabel}
          onBack={() => navigate('/catalog')}
          onEdit={() => navigate(`/catalog/${itemId}/edit`)}
          onDelete={handleDelete}
          onRemovePrice={handleRemoveCustomerPrice}
          priceSheetOpen={showPricingModal}
          onOpenPriceSheet={() => setShowPricingModal(true)}
          onClosePriceSheet={() => setShowPricingModal(false)}
          newPrice={newPrice} setNewPrice={setNewPrice} onSubmitPrice={handleAddCustomerPrice}
        />
        {dialogs}
      </>
    );
  }

  return (
    <Screen data-testid="catalog-item-detail-page">
      <CommandBar
        title={item.name}
        subtitle={`${getProductTypeName(item.product_type, true)} · ${item.product_type}`}
        onBack={() => navigate('/catalog')}
        badges={
          <span className={`text-[11px] font-medium ${
            stock === 0 ? 'text-red-600' : lowStock ? 'text-amber-600' : 'text-emerald-600'
          }`}>
            {stock.toLocaleString()} in stock
          </span>
        }
        actions={[
          { key: 'price', label: 'Add customer price', icon: Plus, onClick: () => setShowPricingModal(true) },
          { key: 'edit', label: 'Edit', icon: Edit, tone: 'primary', onClick: () => navigate(`/catalog/${itemId}/edit`) },
          { key: 'delete', label: 'Delete', icon: Trash2, tone: 'danger', onClick: handleDelete },
        ]}
      />

      <FactBand>
        <Fact label="Code">{item.code || item.id?.slice(0, 8)}</Fact>
        <Fact label="Customer">{item.customer_name || 'General'}</Fact>
        <Fact label="Stock" valueClassName={stock === 0 ? 'text-red-600 font-medium' : lowStock ? 'text-amber-600 font-medium' : undefined}>
          {stock.toLocaleString()}
        </Fact>
        <Fact label="Minimum">{threshold || '—'}</Fact>
        <Fact label="Default price">{item.default_price ? money(item.default_price) : '—'}</Fact>
        <Fact label="Status">{item.status || 'active'}</Fact>
        <Fact label="Created">{item.created_at ? formatIST(item.created_at, { dateOnly: true }) : '—'}</Fact>
        <Fact label="Updated">{item.updated_at ? formatIST(item.updated_at, { dateOnly: true }) : '—'}</Fact>
      </FactBand>

      <ScreenSplit
        railWidth="21rem"
        rail={
          <>
            <Pane title="Customer prices" aside={`${item.customer_prices?.length || 0}`} className="shrink-0">
              {item.customer_prices?.length ? item.customer_prices.map(cp => (
                <div key={cp.customer_id}
                  className="flex items-center justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
                  <span className="min-w-0">
                    <span className="block truncate text-[12px] text-slate-700">{cp.customer_name}</span>
                    <span className="block text-[10px] text-slate-400">per piece</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    <span className="text-[12px] font-bold tabular-nums text-slate-800">
                      {money(cp.price_per_piece)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomerPrice(cp.customer_id)}
                      aria-label={`Remove price for ${cp.customer_name}`}
                      className="rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              )) : (
                <p className="p-3 text-center text-[11px] text-slate-400">
                  No customer-specific prices — everyone pays the default.
                </p>
              )}
            </Pane>

            <Pane title="Stock movements" aside={`${transactions.length}`} className="min-h-0 flex-1">
              {transactions.length === 0 ? (
                <p className="p-3 text-center text-[11px] text-slate-400">No movements recorded</p>
              ) : transactions.map((t, i) => (
                <div key={t.id || i}
                  className="flex items-start justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
                  <span className="min-w-0">
                    <span className="block truncate text-[11px] text-slate-600">
                      {(t.reason || t.type || '').replace(/_/g, ' ')}
                    </span>
                    <span className="block truncate text-[10px] text-slate-400">
                      {t.created_by_name || '—'}
                      {t.created_at ? ` · ${formatIST(t.created_at, { compact: true })}` : ''}
                    </span>
                  </span>
                  <span className={`shrink-0 text-[12px] font-bold tabular-nums ${
                    (t.adjustment_type || t.type) === 'add' ? 'text-emerald-600' : 'text-red-500'
                  }`}>
                    {(t.adjustment_type || t.type) === 'add' ? '+' : '−'}{t.quantity}
                  </span>
                </div>
              ))}
            </Pane>
          </>
        }
      >
        <Pane title="Specifications" aside="what carries onto every order and job card" bodyClassName="p-1.5">
          {hasOrderFields && (
            <>
              <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Asked at order entry
              </p>
              <FactBand className="mb-3">{specRows(orderFields, false)}</FactBand>
            </>
          )}
          {hasJobCardFields && (
            <>
              <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Filled in on the job card
              </p>
              <FactBand>{specRows(jobCardFields, true)}</FactBand>
            </>
          )}
          {!hasOrderFields && !hasJobCardFields && (
            <p className="py-10 text-center text-xs text-slate-400">
              No specifications saved on this product yet.
            </p>
          )}
        </Pane>
      </ScreenSplit>

      <SidePane
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        title="Add customer price"
        subtitle={item.name}
        width="24rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setShowPricingModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAddCustomerPrice}
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              Add price
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Customer</span>
            <Select value={newPrice.customer_id}
              onValueChange={v => setNewPrice(p => ({ ...p, customer_id: v }))}>
              <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
              <SelectContent>
                {customers
                  .filter(c => !item.customer_prices?.some(cp => cp.customer_id === c.id))
                  .map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">
                      {c.customer_name}{c.firm_name ? ` — ${c.firm_name}` : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Price per piece</span>
            <Input type="number" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={newPrice.price_per_piece}
              onChange={e => setNewPrice(p => ({ ...p, price_per_piece: e.target.value }))} />
          </label>
          <p className="text-[11px] text-slate-500">
            This price is used instead of the default whenever this customer orders this product.
          </p>
        </div>
      </SidePane>
      {dialogs}
    </Screen>
  );
};

export default CatalogItemDetailPage;
